(() => {
  const MATERIALS = ['base', '10k', '14k'];
  function intent(term) {
    let material = 'all';
    const query = term.replace(/\b(10|14)\s*(?:k(?:t)?|karat|carat)\b/gi, (_, karat) => {
      material = `${karat}k`;
      return '';
    });
    return { material, query: (material === 'all' ? query : query.replace(/\b(?:solid\s+)?gold\b/gi, '')).replace(/\s+/g, ' ').trim() };
  }
  function groupProducts(products, material = 'all') {
    const families = new Map();
    for (const product of products) {
      if (!MATERIALS.includes(product.material)) continue;
      const key = product.family;
      if (!families.has(key)) families.set(key, { key, title: product.title, products: {} });
      families.get(key).products[product.material] = product;
    }
    return [...families.values()].filter(family => material === 'all' || family.products[material]);
  }
  function relevantFamilies(products, term, material) {
    const families = groupProducts(products, material);
    const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const tokens = normalize(intent(term).query).match(/[\p{L}\p{N}]+/gu) || [];
    if (!tokens.length) return families;
    const matching = families.filter(family => {
      const words = normalize(family.title).match(/[\p{L}\p{N}]+/gu) || [];
      return tokens.every(token => words.some(word => word.startsWith(token)));
    });
    return matching.length ? matching : families;
  }
  function titleQuery(term) {
    const tokens = intent(term).query.match(/[\p{L}\p{N}]+/gu) || [];
    return tokens.map(token => `title:${token}*`).join(' AND ');
  }
  function submissionMaterial(term, previousTerm, material) {
    const inferred = intent(term).material;
    return inferred !== 'all' && intent(previousTerm).material !== inferred ? inferred : material;
  }
  function supportsProductSearch(type) {
    return !type || type.split(',').includes('product');
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { intent, groupProducts, relevantFamilies, titleQuery, submissionMaterial, supportsProductSearch };
  if (typeof document === 'undefined') return;
  const configNode = document.getElementById('suri-search-config');
  if (!configNode) return;
  const { labels, searchUrl } = JSON.parse(configNode.textContent);
  const cache = new Map();
  const states = new WeakMap();
  const formStates = new WeakMap();
  let instanceSequence = 0;
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const normalizeMaterial = value => ['all', ...MATERIALS].includes(value) ? value : 'all';
  const selectedMaterial = term => normalizeMaterial(new URL(location.href).searchParams.get('suri_material') || intent(term).material);
  function resultsUrl(term, material, page = 1) {
    const url = new URL(searchUrl, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('suri_material', material);
    if (page > 1) url.searchParams.set('suri_page', String(page));
    return url;
  }
  async function fetchProducts(query, signal) {
    const products = [];
    let pages = 1;
    for (let page = 1; page <= pages; page++) {
      const url = resultsUrl(query, 'all');
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', query || '*');
      url.searchParams.set('section_id', 'suri-search-data');
      url.searchParams.set('page', String(page));
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Search ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const dataNode = doc.querySelector('[data-suri-search-data]');
      if (!dataNode) throw new Error('Search response unavailable');
      const data = JSON.parse(dataNode.textContent);
      pages = data.pages || 1;
      products.push(...data.products);
    }
    const unique = [...new Map(products.map(product => [product.id, product])).values()];
    return unique;
  }
  async function loadProducts(term, signal) {
    const query = intent(term).query;
    const key = `${document.documentElement.lang}:${query.toLowerCase()}`;
    if (cache.has(key)) return cache.get(key);
    const scoped = titleQuery(term);
    let products = await fetchProducts(scoped || '*', signal);
    if (!products.length && query) products = await fetchProducts(query, signal);
    cache.set(key, products);
    if (cache.size > 12) cache.delete(cache.keys().next().value);
    return products;
  }
  async function loadSuggestions(term, signal) {
    const query = intent(term).query || term;
    const key = `suggest:${document.documentElement.lang}:${query.toLowerCase()}`;
    if (cache.has(key)) return cache.get(key);
    const url = new URL(`${searchUrl.replace(/\/$/, '')}/suggest`, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('section_id', 'suri-search-data');
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Search ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const dataNode = doc.querySelector('[data-suri-search-data]');
    if (!dataNode) throw new Error('Search response unavailable');
    const products = JSON.parse(dataNode.textContent).products;
    cache.set(key, products);
    if (cache.size > 12) cache.delete(cache.keys().next().value);
    return products;
  }
  function controls(material, change) {
    const fieldset = element('fieldset', 'suri-ms-filters');
    fieldset.append(element('legend', 'suri-ms-legend', labels.material));
    for (const value of ['all', ...MATERIALS]) {
      const button = element('button', 'suri-ms-chip');
      button.setAttribute('aria-label', labels[value]);
      button.title = labels[value];
      const compactLabel = value === 'base' ? labels[value].replace(/\s*&\s*/, '/') : value === 'all' ? labels[value] : value.toUpperCase();
      button.append(element('span', 'suri-ms-filter-full', labels[value]), element('span', 'suri-ms-filter-compact', compactLabel));
      button.type = 'button';
      button.dataset.material = value;
      button.setAttribute('aria-pressed', String(value === material));
      button.addEventListener('click', () => change(value));
      fieldset.append(button);
    }
    return fieldset;
  }
  function card(family, material) {
    let choice = material === 'all' ? MATERIALS.find(value => family.products[value]?.available) || MATERIALS.find(value => family.products[value]) : material;
    const article = element('article', 'suri-ms-card');
    article.dataset.family = family.key;
    const photo = element('a', 'suri-ms-photo');
    const image = element('img');
    image.width = 600;
    image.height = 600;
    image.loading = 'lazy';
    image.alt = family.title;
    photo.append(image);
    const heading = element('h3', 'suri-ms-title');
    const link = element('a', '', family.title);
    heading.append(link);
    const price = element('p', 'suri-ms-price');
    const detail = element('p', 'suri-ms-detail');
    const choices = element('div', 'suri-ms-choices');
    choices.setAttribute('role', 'group');
    choices.setAttribute('aria-label', `${family.title}: ${labels.material}`);
    function update() {
      const product = family.products[choice];
      const url = new URL(product.url, location.origin);
      photo.href = link.href = url.href;
      image.src = product.image;
      image.alt = `${family.title} · ${labels[choice]}`;
      price.textContent = `${product.varies ? `${labels.from} ` : ''}${product.price}`;
      detail.textContent = `${labels[choice]}${product.available ? '' : ` · ${labels.soldOut}`}`;
      for (const button of choices.children) button.setAttribute('aria-pressed', String(button.dataset.material === choice));
      article.dataset.material = choice;
    }
    for (const value of MATERIALS) {
      if (!family.products[value]) continue;
      const compactLabel = value === 'base' ? labels[value].replace(/\s*&\s*/, '/') : value.toUpperCase();
      const button = element('button', 'suri-ms-choice', compactLabel);
      button.setAttribute('aria-label', labels[value]);
      button.title = labels[value];
      button.type = 'button';
      button.dataset.material = value;
      button.addEventListener('click', () => { choice = value; update(); });
      choices.append(button);
    }
    article.append(photo, heading, price, detail, choices);
    update();
    return article;
  }
  function render(state, focusMaterial) {
    const { root, products, material, term, full } = state;
    const families = relevantFamilies(products, term, material);
    const size = full ? 24 : 6;
    const pages = Math.max(1, Math.ceil(families.length / size));
    state.page = Math.min(Math.max(1, state.page || 1), pages);
    root.replaceChildren();
    root.classList.add('suri-ms');
    const filters = controls(material, value => {
      state.material = value;
      state.page = 1;
      if (full) history.replaceState(null, '', resultsUrl(term, value));
      syncForm(state.form, value, term);
      render(state, value);
    });
    root.append(filters);
    const status = element('p', 'suri-ms-status', families.length ? (families.length === 1 ? labels.oneResult : labels.results.replace('[count]', String(families.length))) : labels.noResults);
    status.setAttribute('role', 'status');
    root.append(status);
    const grid = element('div', 'suri-ms-grid');
    for (const family of families.slice((state.page - 1) * size, state.page * size)) grid.append(card(family, material));
    root.append(grid);
    if (!full) {
      const all = element('a', 'suri-ms-view-all', labels.viewAll);
      all.href = resultsUrl(term, material).href;
      root.append(all);
      const count = element('span', 'visually-hidden', status.textContent);
      count.setAttribute('data-predictive-search-live-region-count-value', '');
      root.append(count);
    } else if (pages > 1) {
      const nav = element('nav', 'suri-ms-pagination');
      nav.setAttribute('aria-label', `${labels.previous} / ${labels.next}`);
      for (const [delta, text] of [[-1, labels.previous], [1, labels.next]]) {
        const button = element('button', 'suri-ms-chip', text);
        button.type = 'button';
        button.disabled = state.page + delta < 1 || state.page + delta > pages;
        button.addEventListener('click', () => {
          state.page += delta;
          history.replaceState(null, '', resultsUrl(term, material, state.page));
          render(state);
          root.querySelector('.suri-ms-status').setAttribute('tabindex', '-1');
          root.querySelector('.suri-ms-status').focus();
          root.scrollIntoView({ block: 'start' });
        });
        nav.append(button);
      }
      nav.insertBefore(element('span', '', `${state.page} / ${pages}`), nav.lastChild);
      root.append(nav);
    }
    const content = element('a', 'suri-ms-content-results suri-ms-view-all', `${labels.articles} / ${labels.pages}`);
    const contentUrl = resultsUrl(term, 'all');
    contentUrl.searchParams.set('type', 'article,page');
    contentUrl.searchParams.delete('suri_material');
    content.href = contentUrl.href;
    root.append(content);
    if (focusMaterial) filters.querySelector(`[data-material="${focusMaterial}"]`).focus();
  }
  function syncForm(form, material, term) {
    let input = form.querySelector('input[name="suri_material"]');
    if (!input) {
      input = element('input');
      input.type = 'hidden';
      input.name = 'suri_material';
      form.append(input);
    }
    input.value = material;
    formStates.set(form, { term, material });
  }
  document.addEventListener('submit', event => {
    const form = event.target.closest('form[role="search"]');
    if (!form) return;
    const term = form.querySelector('[name="q"]')?.value.trim() || '';
    const previous = formStates.get(form);
    const material = previous ? submissionMaterial(term, previous.term, previous.material) : intent(term).material;
    syncForm(form, material, term);
  }, true);
  async function predictive(instance, term) {
    let previous = states.get(instance);
    previous?.controller?.abort();
    const controller = new AbortController();
    const formState = formStates.get(instance.input.form);
    const material = formState ? submissionMaterial(term, formState.term, formState.material) : intent(term).material;
    const state = { term, material, instance, form: instance.input.form, controller, page: 1 };
    states.set(instance, state);
    syncForm(instance.input.form, material, term);
    instance.setLiveRegionLoadingState();
    instance.hidePromoBlock();
    const root = element('div', 'suri-ms');
    root.id = previous?.root?.id || `suri-material-results-${++instanceSequence}`;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', labels.resultsTitle);
    root.append(element('p', 'suri-ms-status', labels.loading));
    state.root = root;
    instance.input.setAttribute('aria-controls', root.id);
    instance.input.setAttribute('aria-haspopup', 'dialog');
    instance.input.removeAttribute('aria-owns');
    instance.predictiveSearchResults.replaceChildren(root);
    instance.open();
    try {
      state.products = await loadSuggestions(term, controller.signal);
      if (states.get(instance) !== state || instance.getQuery() !== term) return;
      render(state);
      instance.predictiveSearchResults.replaceChildren(root);
      instance.setAttribute('results', 'true');
      instance.setLiveRegionResults();
      instance.open();
      instance.calcHeight();
    } catch (error) {
      if (error.name === 'AbortError' || states.get(instance) !== state || instance.getQuery() !== term) return;
      instance.removeAttribute('loading');
      instance.getNativeSearchResults(term);
    }
  }
  async function initializePage() {
    if (!supportsProductSearch(new URL(location.href).searchParams.get('type'))) return;
    const root = document.querySelector('[data-suri-search-page]');
    if (!root) return;
    const term = root.dataset.query;
    if (!term) return;
    const section = root.closest('.template-search');
    const state = { root, term, full: true, material: selectedMaterial(term), page: Number(new URL(location.href).searchParams.get('suri_page')) || 1 };
    const form = section.querySelector('form[role="search"]');
    state.form = form;
    syncForm(form, state.material, term);
    try {
      state.products = await loadProducts(term);
      render(state);
      root.hidden = false;
      section.classList.add('suri-ms-enhanced');
      section.classList.remove('template-search--empty');
      document.title = `${term} · SURI`;
    } catch { root.hidden = true; }
  }
  window.SuriMaterialSearch = { predictive };
  initializePage();
})();
